import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

function todayVNString(prefix = 'Hà Nội') {
  const now = new Date()
  const d = now.getDate()
  const m = now.getMonth() + 1
  const y = now.getFullYear()
  return `${prefix}, ngày ${d} tháng ${m} năm ${y}`
}

function safeString(v) {
  if (v === null || v === undefined) return ''
  return String(v)
}
function formatPct(n) {
  if (n === null || n === undefined || isNaN(n)) return ''
  const val = Math.round(n * 100) / 100
  return Number(val).toFixed(2)
}
function yearStringForFilename(selectedSemester) {
  if (!selectedSemester) return ''
  let name = ''
  if (
    selectedSemester.schoolYear &&
    typeof selectedSemester.schoolYear === 'object' &&
    selectedSemester.schoolYear.name
  ) {
    name = selectedSemester.schoolYear.name
  } else if (selectedSemester.schoolYear && typeof selectedSemester.schoolYear === 'string') {
    name = selectedSemester.schoolYear
  }
  return String(name).replace(/\D/g, '')
}
function semesterNumberFromTitle(title) {
  if (!title) return ''
  return String(title).replace(/\D/g, '')
}
function parseVNName(full = '') {
  const parts = String(full)
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
  if (parts.length === 0) return { ten: '', hoDem: '' }
  const ten = parts[parts.length - 1]
  const hoDem = parts.slice(0, -1).join(' ')
  return { ten, hoDem }
}

export async function generateTeacherWorkbookAndSaveAction({ dispatch }, { marks, selectedSemester } = {}) {
  try {
    let data = Array.isArray(marks) ? marks : []
    if (!data || data.length === 0) {
      if (!selectedSemester || !selectedSemester.id)
        throw new Error('Missing selectedSemester when fetching marks for teacher report')
      const res = await dispatch('fetchMarks', { semester: selectedSemester.id, limit: -1 })
      data = Array.isArray(res) ? res : []
    }

    const groups = {}
    for (let i = 0; i < data.length; i++) {
      const rec = data[i] || {}
      const teacher = rec.teacher || {}
      const teacherId = teacher.id || teacher._id || ''
      const teacherCode = teacher.code || ''
      const teacherName = teacher.name || ''
      const subject = rec.subject || {}
      const subjectId = subject.id || subject._id || ''
      const subjectTitle = subject.title || ''
      const cls = rec['class'] || rec.class || {}
      const classId = cls.id || cls._id || ''
      const classTitle = cls.title || ''
      const factor = rec.factor || {}
      const factorType = factor.type || ''
      const student = rec.student || {}
      const studentId = student.id || student._id || null

      const key = teacherId + '||' + subjectId + '||' + classId
      if (!groups[key]) {
        groups[key] = {
          teacherId,
          teacherCode,
          teacherName,
          subjectId,
          subjectTitle,
          classId,
          classTitle,
          studentsMap: {},
          factorEntryCounts: { 'factor-1': 0, 'factor-2': 0, 'factor-3': 0 },
          expectedTxPerStudent: 1
        }
      }
      const g = groups[key]
      if (studentId) g.studentsMap[studentId] = true
      if (factorType) {
        g.factorEntryCounts[factorType] = (g.factorEntryCounts[factorType] || 0) + 1
        if (factor.quantity !== undefined && factor.quantity !== null) {
          g.expectedTxPerStudent = Number(factor.quantity) || g.expectedTxPerStudent
        }
      }
    }

    const STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY = {
      Toán: 6,
      'Vật lý': 4,
      'Hóa học': 4,
      'Sinh học': 4,
      'Ngữ văn': 4,
      'Ngoại ngữ': 6,
      'Lịch sử': 3,
      'Địa lí': 3,
      GDCD: 2
    }

    const rows = []
    const collator = new Intl.Collator('vi', { sensitivity: 'base' })
    const orderedGroups = Object.values(groups).sort((a, b) => {
      const na = parseVNName(a.teacherName || '')
      const nb = parseVNName(b.teacherName || '')
      const byTen = collator.compare(na.ten, nb.ten)
      if (byTen !== 0) return byTen
      const byHoDem = collator.compare(na.hoDem, nb.hoDem)
      if (byHoDem !== 0) return byHoDem
      const bySubject = collator.compare(a.subjectTitle || '', b.subjectTitle || '')
      if (bySubject !== 0) return bySubject
      const byClass = collator.compare(a.classTitle || '', b.classTitle || '')
      if (byClass !== 0) return byClass
      return collator.compare(a.teacherCode || '', b.teacherCode || '')
    })

    let idx = 1
    for (const g of orderedGroups) {
      const totalStudents = Object.keys(g.studentsMap).length
      const totalFactor1Entries = g.factorEntryCounts['factor-1'] || 0
      const totalFactor2Entries = g.factorEntryCounts['factor-2'] || 0
      const totalFactor3Entries = g.factorEntryCounts['factor-3'] || 0

      let standardTxPerStudent = 1
      const subjTitle = g.subjectTitle || ''
      for (const key in STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY) {
        if (!Object.prototype.hasOwnProperty.call(STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY, key)) continue
        if (subjTitle.toLowerCase().includes(String(key).toLowerCase())) {
          standardTxPerStudent = STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY[key]
          break
        }
      }

      const expectedTotalFactor1 = standardTxPerStudent * Math.max(1, totalStudents)
      const pctTx = expectedTotalFactor1 > 0 ? (totalFactor1Entries / expectedTotalFactor1) * 100 : 0

      const expectedPerStudentGkCk =
        g.expectedTxPerStudent && Number(g.expectedTxPerStudent) > 0 ? Number(g.expectedTxPerStudent) : 1
      const expectedTotalFactor2 = expectedPerStudentGkCk * Math.max(1, totalStudents)
      const expectedTotalFactor3 = expectedPerStudentGkCk * Math.max(1, totalStudents)
      const pctGk = expectedTotalFactor2 > 0 ? (totalFactor2Entries / expectedTotalFactor2) * 100 : 0
      const pctCk = expectedTotalFactor3 > 0 ? (totalFactor3Entries / expectedTotalFactor3) * 100 : 0

      rows.push([
        idx,
        String(g.teacherCode || ''),
        String(g.teacherName || ''),
        String(g.subjectTitle || ''),
        String(g.classTitle || ''),
        Object.keys(g.studentsMap).length,
        formatPct(pctTx),
        formatPct(pctGk),
        formatPct(pctCk),
        '',
        ''
      ])
      idx++
    }

    const wb = XLSX.utils.book_new()
    const totalCols = 11
    const titleText = 'BÁO CÁO THỐNG KÊ TIẾN ĐỘ VÀO ĐIỂM THEO GIÁO VIÊN'
    const semesterTitle =
      (selectedSemester.title ? String(selectedSemester.title) : '') +
      (selectedSemester.schoolYear.name ? ' - ' + String(selectedSemester.schoolYear.name) : '')

    const makeEmpty = () => Array.from({ length: totalCols }).map(() => '')
    const topRows = []
    const r0 = makeEmpty()
    r0[0] = 'SỞ GIÁO DỤC VÀ ĐÀO TẠO HÀ NỘI'
    const r1 = makeEmpty()
    r1[0] = 'THPT CHUYÊN ĐẠI HỌC SƯ PHẠM'
    const r2 = makeEmpty()
    r2[0] = titleText
    const r3 = makeEmpty()
    r3[0] = semesterTitle
    topRows.push(r0, r1, r2, r3, makeEmpty())

    const headerRow6 = [
      'STT',
      'Mã nhân sự',
      'Giáo viên',
      'Môn học',
      'Lớp',
      'Số HS đ.giá',
      'ĐĐG \ntx (%)',
      'ĐĐG \ngk (%)',
      'ĐĐG \nck (%)',
      'Chuyên đề',
      ''
    ]
    const headerRow7 = ['', '', '', '', '', '', '', '', '', 'Số HS đ.giá', 'TL(%)']

    const allRows = topRows.concat([headerRow6, headerRow7]).concat(rows)
    const ws = XLSX.utils.aoa_to_sheet(allRows)

    ws['!merges'] = ws['!merges'] || []
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } })
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 3 } })
    ws['!merges'].push({ s: { r: 2, c: 0 }, e: { r: 2, c: 10 } })
    ws['!merges'].push({ s: { r: 3, c: 0 }, e: { r: 3, c: 10 } })

    const headerStartRowIndex = topRows.length
    ws['!merges'].push({ s: { r: headerStartRowIndex, c: 9 }, e: { r: headerStartRowIndex, c: 10 } })

    ws['!cols'] = [
      { wch: 5 },
      { wch: 12 },
      { wch: 22 },
      { wch: 12 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 }
    ]

    ws['!rows'] = ws['!rows'] || []
    ws['!rows'][0] = { hpt: 18 }
    ws['!rows'][1] = { hpt: 18 }
    ws['!rows'][2] = { hpt: 22 }
    ws['!rows'][3] = { hpt: 18 }
    ws['!rows'][headerStartRowIndex] = { hpt: 24 }
    ws['!rows'][headerStartRowIndex + 1] = { hpt: 22 }

    const titleCells = [
      { r: 0, c: 0, sz: 11, bold: true },
      { r: 1, c: 0, sz: 11, bold: true },
      { r: 2, c: 0, sz: 13, bold: true },
      { r: 3, c: 0, sz: 12, bold: true }
    ]
    titleCells.forEach(t => {
      const ref = XLSX.utils.encode_cell({ r: t.r, c: t.c })
      ws[ref] = ws[ref] || { t: 's', v: ws[ref].v || '' }
      ws[ref].s = ws[ref].s || {}
      ws[ref].s.font = Object.assign({}, ws[ref].s.font, { bold: t.bold, sz: t.sz })
      ws[ref].s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true }
    })

    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    }
    const dataStartRow = headerStartRowIndex + 2
    const dataEndRow = dataStartRow + rows.length - 1
    const tableStartRow = headerStartRowIndex
    const tableEndRow = Math.max(headerStartRowIndex + 1, dataEndRow)

    for (let R = tableStartRow; R <= tableEndRow; ++R) {
      for (let C = 0; C < totalCols; ++C) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C })
        if (!ws[ref]) ws[ref] = { t: 's', v: '' }
        ws[ref].s = ws[ref].s || {}
        ws[ref].s.border = borderStyle
        if (R === headerStartRowIndex || R === headerStartRowIndex + 1) {
          ws[ref].s.font = Object.assign({}, ws[ref].s.font, { bold: true })
          ws[ref].s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true }
        }
      }
    }

    const leftCols = [1, 2, 3, 4]
    const centerCols = [0, 5, 9, 10]
    const rightPctCols = [6, 7, 8]

    for (let r = dataStartRow; r <= dataEndRow; r++) {
      centerCols.forEach(c => {
        const ref = XLSX.utils.encode_cell({ r, c })
        ws[ref] = ws[ref] || { t: 's', v: '' }
        ws[ref].s = ws[ref].s || {}
        ws[ref].s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true }
        ws[ref].s.border = borderStyle
      })
      leftCols.forEach(c => {
        const ref = XLSX.utils.encode_cell({ r, c })
        ws[ref] = ws[ref] || { t: 's', v: '' }
        ws[ref].s = ws[ref].s || {}
        ws[ref].s.alignment = { horizontal: 'left', vertical: 'center', wrapText: true }
        ws[ref].s.border = borderStyle
      })
      rightPctCols.forEach(c => {
        const ref = XLSX.utils.encode_cell({ r, c })
        ws[ref] = ws[ref] || { t: 's', v: '' }
        ws[ref].s = ws[ref].s || {}
        ws[ref].s.alignment = { horizontal: 'right', vertical: 'center', wrapText: true }
        ws[ref].s.border = borderStyle
      })
    }

    const mergeVertical = colIndex => {
      let r = dataStartRow
      while (r <= dataEndRow) {
        const startRef = XLSX.utils.encode_cell({ r, c: colIndex })
        const startVal = ws[startRef] ? (ws[startRef].v === undefined ? '' : String(ws[startRef].v)) : ''
        if (startVal === '') {
          r++
          continue
        }
        let end = r
        while (end + 1 <= dataEndRow) {
          const nextRef = XLSX.utils.encode_cell({ r: end + 1, c: colIndex })
          const nextVal = ws[nextRef] ? (ws[nextRef].v === undefined ? '' : String(ws[nextRef].v)) : ''
          if (String(nextVal) === String(startVal)) end++
          else break
        }
        if (end > r) ws['!merges'].push({ s: { r, c: colIndex }, e: { r: end, c: colIndex } })
        r = end + 1
      }
    }
    mergeVertical(1)
    mergeVertical(2)

    const footerRows = []
    const emptyFooter = makeEmpty()
    footerRows.push(emptyFooter)
    const f1 = makeEmpty()
    f1[4] = todayVNString()
    const f2 = makeEmpty()
    f2[1] = 'Người lập biểu'
    f2[4] = 'Hiệu trưởng'
    const f3 = makeEmpty()
    f3[1] = '(Ký, họ tên)'
    f3[4] = '(Ký, họ tên, đóng dấu)'
    footerRows.push(f1, f2, f3)

    let appendRow = (ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']).e.r : dataEndRow) + 1
    footerRows.forEach(fr => {
      for (let c = 0; c < totalCols; c++) {
        const ref = XLSX.utils.encode_cell({ r: appendRow, c })
        ws[ref] = ws[ref] || { t: 's', v: '' }
        ws[ref].v = fr[c] || ''
        ws[ref].s = ws[ref].s || {}
        ws[ref].s.alignment = { horizontal: 'center', vertical: 'center' }
      }
      appendRow++
    })

    const f1Row = appendRow - 3
    const f2Row = appendRow - 2
    const f3Row = appendRow - 1
    ws['!merges'].push(
      { s: { r: f1Row, c: 4 }, e: { r: f1Row, c: 10 } },
      { s: { r: f2Row, c: 1 }, e: { r: f2Row, c: 2 } },
      { s: { r: f2Row, c: 4 }, e: { r: f2Row, c: 10 } },
      { s: { r: f3Row, c: 1 }, e: { r: f3Row, c: 2 } },
      { s: { r: f3Row, c: 4 }, e: { r: f3Row, c: 10 } }
    )

    const f1Ref = XLSX.utils.encode_cell({ r: f1Row, c: 4 })
    ws[f1Ref].s = Object.assign({}, ws[f1Ref].s, {
      font: Object.assign({}, ws[f1Ref].s.font, { italic: true }),
      alignment: { horizontal: 'right', vertical: 'center', wrapText: true }
    })
    const f2RefL = XLSX.utils.encode_cell({ r: f2Row, c: 1 })
    ws[f2RefL].s = Object.assign({}, ws[f2RefL].s, {
      font: Object.assign({}, ws[f2RefL].s.font, { bold: true }),
      alignment: { horizontal: 'center', vertical: 'center' }
    })
    const f2RefR = XLSX.utils.encode_cell({ r: f2Row, c: 4 })
    ws[f2RefR].s = Object.assign({}, ws[f2RefR].s, {
      font: Object.assign({}, ws[f2RefR].s.font, { bold: true }),
      alignment: { horizontal: 'right', vertical: 'center' }
    })
    const f3RefL = XLSX.utils.encode_cell({ r: f3Row, c: 1 })
    ws[f3RefL].s = Object.assign({}, ws[f3RefL].s, {
      font: Object.assign({}, ws[f3RefL].s.font, { italic: true }),
      alignment: { horizontal: 'center', vertical: 'center' }
    })
    const f3RefR = XLSX.utils.encode_cell({ r: f3Row, c: 4 })
    ws[f3RefR].s = Object.assign({}, ws[f3RefR].s, {
      font: Object.assign({}, ws[f3RefR].s.font, { italic: true }),
      alignment: { horizontal: 'right', vertical: 'center' }
    })

    const finalRange = XLSX.utils.decode_range(
      ws['!ref'] || XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } })
    )
    finalRange.e.r = Math.max(finalRange.e.r, appendRow - 1)
    ws['!ref'] = XLSX.utils.encode_range(finalRange)

    XLSX.utils.book_append_sheet(wb, ws, 'Báo cáo')

    const yearFile = yearStringForFilename(selectedSemester)
    const semNum = semesterNumberFromTitle(selectedSemester.title ? selectedSemester.title : '')
    const filename =
      'BAO_CAO_THONG_KE_TIEN_DO_VAO_DIEM_THEO_GV_HOCKY_' + semNum + '_NAMHOC_' + (yearFile || '') + '.xlsx'

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true })
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename)
    return wb
  } catch (err) {
    console.error('generateTeacherWorkbookAndSave error', err)
    throw err
  }
}

export async function generateSchoolWorkbookAndSaveAction({ dispatch }, { marks, selectedSemester } = {}) {
  try {
    let data = Array.isArray(marks) ? marks : []
    if (!data || data.length === 0) {
      if (!selectedSemester || !selectedSemester.id)
        throw new Error('Missing selectedSemester when fetching marks for school report')
      const res = await dispatch('fetchMarks', { semester: selectedSemester.id, limit: -1 })
      data = Array.isArray(res) ? res : []
    }

    const STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY = {
      Toán: 6,
      'Vật lý': 4,
      'Hóa học': 4,
      'Sinh học': 4,
      'Ngữ văn': 4,
      'Ngoại ngữ': 6,
      'Tiếng Anh': 6,
      'Lịch sử': 3,
      'Địa lí': 3,
      GDCD: 2
    }
    const getStandardTxQ = (title = '') => {
      const t = String(title).toLowerCase()
      for (const k in STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY) {
        if (!Object.prototype.hasOwnProperty.call(STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY, k)) continue
        if (t.indexOf(String(k).toLowerCase()) !== -1) return STANDARD_POINTS_BY_SUBJECT_FOR_LOWER_SECONDARY[k]
      }
      return 1
    }

    const SUBJECT_ORDER = [
      'Toán',
      'Ngữ văn',
      'Ngoại ngữ',
      'GDCD',
      'Công nghệ',
      'Tin học',
      'Khoa học',
      'Vật lý',
      'Hóa học',
      'Sinh học',
      'Lịch sử',
      'Địa lí'
    ]
    const allSubjectsInDataSet = new Set()
    data.forEach(rec => {
      const st = rec.subject && rec.subject.title
      if (st) allSubjectsInDataSet.add(String(st))
    })
    const SUBJECTS = [
      ...SUBJECT_ORDER.filter(s => allSubjectsInDataSet.has(s)),
      ...Array.from(allSubjectsInDataSet).filter(s => !SUBJECT_ORDER.includes(s))
    ]

    const classes = {}
    for (const rec of data) {
      const subjectTitle = safeString(rec.subject && rec.subject.title)
      if (!SUBJECTS.includes(subjectTitle)) continue
      const cls = rec.class || {}
      const classTitle = safeString(cls.title)
      const grade = (classTitle.match(/\d+/) || [''])[0] || ''
      const factorType = safeString(rec.factor && rec.factor.type)
      const qty = rec.factor && rec.factor.quantity
      const studentId = (rec.student && (rec.student.id || rec.student._id)) || null
      const key = grade + '||' + classTitle
      if (!classes[key]) {
        classes[key] = {
          grade,
          classTitle,
          students: new Set(),
          subj: {}
        }
      }
      const g = classes[key]
      if (studentId) g.students.add(studentId)
      if (!g.subj[subjectTitle]) g.subj[subjectTitle] = { tx: 0, gk: 0, ck: 0, expectedGkCk: 1 }
      if (factorType === 'factor-1') g.subj[subjectTitle].tx++
      else if (factorType === 'factor-2') {
        g.subj[subjectTitle].gk++
        if (qty !== undefined && qty !== null) g.subj[subjectTitle].expectedGkCk = Number(qty) || 1
      } else if (factorType === 'factor-3') {
        g.subj[subjectTitle].ck++
        if (qty !== undefined && qty !== null) g.subj[subjectTitle].expectedGkCk = Number(qty) || 1
      }
    }

    const rows = []
    let idx = 1
    let totalAll = 0
    const classKeys = Object.keys(classes).sort((a, b) => {
      const [ga, ca] = a.split('||')
      const [gb, cb] = b.split('||')
      return (ga || '').localeCompare(gb || '') || (ca || '').localeCompare(cb || '')
    })
    for (const key of classKeys) {
      const g = classes[key]
      const total = g.students.size
      totalAll += total
      const row = [idx, `Khối ${g.grade}`, g.classTitle, total]
      SUBJECTS.forEach(sub => {
        const s = g.subj[sub] || { tx: 0, gk: 0, ck: 0, expectedGkCk: 1 }
        const qTx = getStandardTxQ(sub)
        const expectedTxTotal = Math.max(1, total) * qTx
        const expectedGkTotal = Math.max(1, total) * (s.expectedGkCk || 1)
        const expectedCkTotal = Math.max(1, total) * (s.expectedGkCk || 1)
        const pctTx = expectedTxTotal > 0 ? (s.tx / expectedTxTotal) * 100 : 0
        const pctGk = expectedGkTotal > 0 ? (s.gk / expectedGkTotal) * 100 : 0
        const pctCk = expectedCkTotal > 0 ? (s.ck / expectedCkTotal) * 100 : 0
        row.push(formatPct(pctTx), formatPct(pctGk), formatPct(pctCk))
      })
      rows.push(row)
      idx++
    }
    const totalRow = ['', '', 'Tổng', totalAll]
    SUBJECTS.forEach(() => totalRow.push('', '', ''))
    rows.push(totalRow)

    const totalCols = 4 + SUBJECTS.length * 3
    const makeEmpty = () => Array.from({ length: totalCols }).map(() => '')
    const topRows = [
      ['UBND PHƯỜNG YÊN HÒA'],
      ['THCS LÝ THÁI TỔ'],
      ['BÁO CÁO THỐNG KÊ TIẾN ĐỘ VÀO ĐIỂM TOÀN TRƯỜNG'],
      [
        `HỌC KỲ ${semesterNumberFromTitle(selectedSemester.title || '') || 'I'} - NĂM HỌC: ${selectedSemester.schoolYear
          .name || ''}`
      ],
      makeEmpty()
    ]
    const header1 = ['STT', 'Khối', 'Lớp', 'Sĩ số']
    const header2 = ['', '', '', '']
    SUBJECTS.forEach(sub => {
      header1.push(sub, '', '')
      header2.push('ĐĐG \ntx', 'ĐĐG \ngk', 'ĐĐG \nck')
    })
    const allRows = topRows.concat([header1, header2], rows)
    const ws = XLSX.utils.aoa_to_sheet(allRows)

    const ensureCell = (r, c) => {
      const ref = XLSX.utils.encode_cell({ r, c })
      if (!ws[ref]) ws[ref] = { t: 's', v: '' }
      if (!ws[ref].s) ws[ref].s = {}
      return ref
    }

    ws['!merges'] = ws['!merges'] || []
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } })
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } })
    ws['!merges'].push({ s: { r: 2, c: 0 }, e: { r: 2, c: totalCols - 1 } })
    ws['!merges'].push({ s: { r: 3, c: 0 }, e: { r: 3, c: totalCols - 1 } })
    for (let i = 0; i < SUBJECTS.length; i++) {
      const startCol = 4 + i * 3
      ws['!merges'].push({ s: { r: 5, c: startCol }, e: { r: 5, c: startCol + 2 } })
    }
    for (let c = 0; c < 4; c++) {
      ws['!merges'].push({ s: { r: 5, c }, e: { r: 6, c } })
    }

    ws['!cols'] = Array(totalCols).fill({ wch: 10 })
    ws['!cols'][0] = { wch: 5 }
    ws['!cols'][1] = { wch: 10 }
    ws['!cols'][2] = { wch: 12 }
    ws['!cols'][3] = { wch: 8 }

    ws['!rows'] = ws['!rows'] || []
    ws['!rows'][2] = { hpt: 22 }
    ws['!rows'][5] = { hpt: 24 }
    ws['!rows'][6] = { hpt: 24 }

    const titleSpecs = [
      { r: 0, sz: 11 },
      { r: 1, sz: 11 },
      { r: 2, sz: 13 },
      { r: 3, sz: 12 }
    ]
    titleSpecs.forEach(t => {
      const ref = ensureCell(t.r, 0)
      ws[ref].s.font = Object.assign({}, ws[ref].s.font, { bold: true, sz: t.sz })
      ws[ref].s.alignment = { horizontal: 'center', vertical: 'center' }
    })

    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    }
    const range = XLSX.utils.decode_range(ws['!ref'])
    for (let R = 5; R <= range.e.r; R++) {
      for (let C = 0; C < totalCols; C++) {
        const ref = ensureCell(R, C)
        ws[ref].s.border = borderStyle
        if (R === 5 || R === 6) {
          ws[ref].s.font = Object.assign({}, ws[ref].s.font, { bold: true })
          ws[ref].s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true }
        } else {
          if (C === 2) ws[ref].s.alignment = { horizontal: 'left', vertical: 'center', wrapText: true }
          else if (C >= 4) ws[ref].s.alignment = { horizontal: 'right', vertical: 'center', wrapText: true }
          else if (C === 3) ws[ref].s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true }
          else ws[ref].s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true }
        }
      }
    }

    const footerStartRow = rows.length + 8
    XLSX.utils.sheet_add_aoa(ws, [makeEmpty()], { origin: { r: footerStartRow, c: 0 } })
    XLSX.utils.sheet_add_aoa(ws, [makeEmpty()], { origin: { r: footerStartRow + 1, c: 0 } })
    const dateRef = ensureCell(footerStartRow + 1, 4)
    ws[dateRef].v = todayVNString()
    ws[dateRef].s.font = Object.assign({}, ws[dateRef].s.font, { italic: true })
    ws[dateRef].s.alignment = { horizontal: 'right', vertical: 'center' }
    ws['!merges'].push({ s: { r: footerStartRow + 1, c: 4 }, e: { r: footerStartRow + 1, c: totalCols - 1 } })

    XLSX.utils.sheet_add_aoa(ws, [makeEmpty()], { origin: { r: footerStartRow + 2, c: 0 } })
    const leftTitleRef = ensureCell(footerStartRow + 2, 1)
    ws[leftTitleRef].v = 'Người lập biểu'
    ws[leftTitleRef].s.font = Object.assign({}, ws[leftTitleRef].s.font, { bold: true })
    ws[leftTitleRef].s.alignment = { horizontal: 'center', vertical: 'center' }
    ws['!merges'].push({ s: { r: footerStartRow + 2, c: 1 }, e: { r: footerStartRow + 2, c: 2 } })

    const rightTitleRef = ensureCell(footerStartRow + 2, 4)
    ws[rightTitleRef].v = 'Hiệu trưởng'
    ws[rightTitleRef].s.font = Object.assign({}, ws[rightTitleRef].s.font, { bold: true })
    ws[rightTitleRef].s.alignment = { horizontal: 'right', vertical: 'center' }
    ws['!merges'].push({ s: { r: footerStartRow + 2, c: 4 }, e: { r: footerStartRow + 2, c: totalCols - 1 } })

    XLSX.utils.sheet_add_aoa(ws, [makeEmpty()], { origin: { r: footerStartRow + 3, c: 0 } })
    const leftSignRef = ensureCell(footerStartRow + 3, 1)
    ws[leftSignRef].v = '(Ký, họ tên)'
    ws[leftSignRef].s.font = Object.assign({}, ws[leftSignRef].s.font, { italic: true })
    ws[leftSignRef].s.alignment = { horizontal: 'center', vertical: 'center' }
    ws['!merges'].push({ s: { r: footerStartRow + 3, c: 1 }, e: { r: footerStartRow + 3, c: 2 } })

    const rightSignRef = ensureCell(footerStartRow + 3, 4)
    ws[rightSignRef].v = '(Ký, họ tên, đóng dấu)'
    ws[rightSignRef].s.font = Object.assign({}, ws[rightSignRef].s.font, { italic: true })
    ws[rightSignRef].s.alignment = { horizontal: 'right', vertical: 'center' }
    ws['!merges'].push({ s: { r: footerStartRow + 3, c: 4 }, e: { r: footerStartRow + 3, c: totalCols - 1 } })

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Báo cáo')

    const yearFile = yearStringForFilename(selectedSemester)
    const semNum = semesterNumberFromTitle(selectedSemester.title || '')
    const filename = `BAO_CAO_THONG_KE_TIEN_DO_VAO_DIEM_TOAN_TRUONG_HOCKY_${semNum}_NAMHOC_${yearFile || ''}.xlsx`
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true })
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename)
    return wb
  } catch (err) {
    console.error('generateSchoolWorkbookAndSave error', err)
    throw err
  }
}
